import React from 'react';
import { Zap, Layout, Users, Brain, MousePointer2, Share2, Layers, Search, Sparkles } from 'lucide-react';

export const FEATURE_PAGES = [
    {
        slug: 'infinite-canvas',
        title: 'The Infinite Canvas for Visual Thinkers',
        subtitle: 'Unbounded Creativity',
        description: 'Stop hitting the edges of your paper. NoteSpace gives you an infinite digital whiteboard to map out complex ideas, organize research, and brainstorm without limits.',
        image: '/hero-preview.png',
        benefits: [
            {
                title: 'Spatial Organization',
                description: 'Arrange your notes, images, and documents spatially to see the big picture and the details simultaneously.',
                icon: <Layout className="w-6 h-6" />
            },
            {
                title: 'Zoom to Detail',
                description: 'Deep dive into specifics with smooth vector-based zooming, then pull back to see the entire landscape of your project.',
                icon: <Search className="w-6 h-6" />
            },
            {
                title: 'Multi-Media Support',
                description: 'Drag and drop images, PDFs, videos, and websites directly onto your canvas alongside text and drawings.',
                icon: <Layers className="w-6 h-6" />
            },
            {
                title: 'Visual Connections',
                description: 'Draw connection lines between any elements to visualize relationships, flows, and dependencies.',
                icon: <Share2 className="w-6 h-6" />
            }
        ],
        howItWorks: [
            { title: 'Create', description: 'Start with a blank canvas or a template designed for your specific workflow.', icon: '1' },
            { title: 'Organize', description: 'Drag and drop any content—notes, images, or PDFs—and arrange them spatially.', icon: '2' },
            { title: 'Connect', description: 'Draw lines between ideas to visualize relationships and build your knowledge map.', icon: '3' }
        ],
        testimonials: [
            { quote: 'NoteSpace changed how I research. Being able to see everything at once is a game changer.', author: 'Sarah Chen', role: 'PhD Researcher' },
            { quote: 'The infinite canvas is exactly what my brain needed. No more folders, just space.', author: 'Marcus Thorne', role: 'Art Director' }
        ],
        faqs: [
            { question: 'Is the canvas truly infinite?', answer: 'Yes! You can pan and zoom across a virtually limitless workspace. Performance remains smooth even with thousands of notes.' },
            { question: 'Can I export my canvas?', answer: 'Absolutely. You can export as high-resolution PNG, PDF, or Markdown files.' }
        ]
    },
    {
        slug: 'ai-study-tools',
        title: 'AI-Powered Study Tools',
        subtitle: 'Learn Faster',
        description: 'Transform your scattered notes into structured knowledge. Our Gemini-powered AI analyzes your board content to create summaries, quizzes, and flashcards instantly.',
        image: '/hero-preview.png',
        benefits: [
            {
                title: 'Smart Summaries',
                description: 'Get instant concise summaries of long documents, lecture notes, or entire board sections.',
                icon: <Sparkles className="w-6 h-6" />
            },
            {
                title: 'Auto-Generated Quizzes',
                description: 'Test your knowledge with AI-generated quizzes based specifically on the content you have collected.',
                icon: <Brain className="w-6 h-6" />
            },
            {
                title: 'Context Aware Answers',
                description: 'Ask questions about your notes and get accurate answers citing specific sources on your board.',
                icon: <Zap className="w-6 h-6" />
            },
            {
                title: 'Study Guides',
                description: 'Automatically convert messy brainstorming sessions into structured, reviewable study guides.',
                icon: <Layout className="w-6 h-6" />
            }
        ],
        howItWorks: [
            { title: 'Add Content', description: 'Upload papers, add notes, or web clippings to your board.', icon: '1' },
            { title: 'Analyze', description: 'Trigger Smart View to let Gemini process your spatial knowledge.', icon: '2' },
            { title: 'Master', description: 'Generate quizzes and flashcards to solidify your understanding.', icon: '3' }
        ],
        testimonials: [
            { quote: 'The AI summaries saved me hours during finals week. It actually understands my notes!', author: 'Alex Rivera', role: 'Med Student' },
            { quote: 'I use the auto-quizzes to prep for team presentations. It finds the gaps in my logic.', author: 'Elena Rossi', role: 'Product Lead' }
        ],
        faqs: [
            { question: 'Which AI models do you use?', answer: 'We use Google Gemini 1.5 Pro and Flash for context-aware processing of your private data.' },
            { question: 'Is my data used for training?', answer: 'No. Your content is processed ephemerally and is never used to train public AI models.' }
        ]
    },
    {
        slug: 'collaboration',
        title: 'Real-Time Collaboration',
        subtitle: 'Work Together',
        description: 'Collaborate with your team or study group in real-time. See cursors, edits, and updates instantly as you build ideas together on a shared canvas.',
        image: '/hero-preview.png',
        benefits: [
            {
                title: 'Live Cursors',
                description: 'See exactly where your teammates are looking and working with real-time cursor tracking.',
                icon: <MousePointer2 className="w-6 h-6" />
            },
            {
                title: 'Instant Sync',
                description: 'Changes populate instantly across all devices. No manual saving or refreshing required.',
                icon: <Zap className="w-6 h-6" />
            },
            {
                title: 'Shared Links',
                description: 'Invite collaborators with a simple link. Control view or edit permissions with one click.',
                icon: <Share2 className="w-6 h-6" />
            },
            {
                title: 'Group Brainstorming',
                description: 'Run effective brainstorming sessions where everyone can contribute sticky notes and doodles simultaneously.',
                icon: <Users className="w-6 h-6" />
            }
        ],
        howItWorks: [
            { title: 'Invite', description: 'Share a collaboration link with your team instantly.', icon: '1' },
            { title: 'Co-Create', description: 'Work together on the same canvas with live cursors and updates.', icon: '2' },
            { title: 'Publish', description: 'Share your final board as a read-only workspace or interactive site.', icon: '3' }
        ],
        testimonials: [
            { quote: 'Our remote design sprints feel like we are in the same room again.', author: 'Jenny Kim', role: 'UX Designer' },
            { quote: 'Collaborating on study guides has never been easier. We all contribute at once.', author: 'David Wu', role: 'Computer Science Student' }
        ],
        faqs: [
            { question: 'How many people can collaborate?', answer: 'Up to 50 people can work on a single board simultaneously with near-zero latency.' },
            { question: 'Can I see edit history?', answer: 'Yes, you can track who changed what and when in the board settings.' }
        ]
    }
];

export const COMPARISON_PAGES = [
    {
        slug: 'notion-vs-notespace',
        competitorName: 'Notion',
        title: 'Notion vs NoteSpace',
        description: 'Notion is great for documents, but NoteSpace is built for visual thinking. See why creative minds prefer the infinite canvas over linear pages.',
        comparisonPoints: [
            { feature: 'Infinite Canvas Interface', us: true, them: false },
            { feature: 'Freeform Drawing', us: true, them: false },
            { feature: 'Spatial Organization', us: true, them: false },
            { feature: 'Structured Databases', us: 'Simple Tables', them: 'Advanced Databases' },
            { feature: 'AI Study Tools', us: true, them: 'Generic AI' },
            { feature: 'Real-time Collaboration', us: true, them: true },
            { feature: 'Offline Mode', us: 'Coming Soon', them: true }
        ],
        whySwitch: [
            { title: 'Visual over Linear', description: 'Stop being confined by vertical pages. Map your thoughts in 2D space.' },
            { title: 'Study Optimized', description: 'While Notion is a general tool, NoteSpace is built specifically for learning and research.' },
            { title: 'Free Creativity', description: 'Get professional spatial tools without the enterprise price tag.' }
        ],
        testimonials: [
            { quote: 'I moved all my research from Notion to NoteSpace. The spatial layout helps me see connections I missed before.', author: 'Dr. Emily Vance', role: 'Neuroscientist' }
        ],
        faqs: [
            { question: 'Can I import from Notion?', answer: 'Yes! You can export your Notion pages as Markdown and drag them directly onto your NoteSpace canvas.' }
        ]
    },
    {
        slug: 'miro-vs-notespace',
        competitorName: 'Miro',
        title: 'Miro vs NoteSpace',
        description: 'Miro is a powerful enterprise tool, but NoteSpace gives you the same visual freedom with better note-taking features and AI study tools—for free.',
        comparisonPoints: [
            { feature: 'Unlimited Free Boards', us: true, them: 'Limited to 3' },
            { feature: 'AI Features on Free Plan', us: true, them: false },
            { feature: 'Text Editor Quality', us: 'Rich Text', them: 'Basic Text' },
            { feature: 'Study Mode', us: true, them: false },
            { feature: 'Complexity', us: 'Simple & Fast', them: 'Enterprise Complex' },
            { feature: 'Mobile Experience', us: 'Optimized', them: 'Standard' }
        ],
        whySwitch: [
            { title: 'Note-Taking Focus', description: 'Miro is for whiteboarding; NoteSpace is for deep, persistent note-taking and learning.' },
            { title: 'Integrated AI', description: 'Our AI is built into the note-taking experience, not just a plugin.' },
            { title: 'Truly Free', description: 'No "3 board limit". Create as much as you need.' }
        ],
        testimonials: [
            { quote: 'NoteSpace is the Miro alternative I was looking for. Faster, simpler, and way better for my individual study sessions.', author: 'Kevin Park', role: 'Design Student' }
        ],
        faqs: [
            { question: 'How does this compare to Miro for teaching?', answer: 'NoteSpace is excellent for educators who need to build complex knowledge maps and share them with students for study.' }
        ]
    }
];

export const USE_CASE_PAGES = [
    {
        slug: 'students',
        title: 'Ace Exams with Infinite Space',
        subtitle: 'Students',
        description: 'Organize your lecture notes, research papers, and study guides in one visual workspace. Use AI to generate flashcards and quizzes instantly.',
        features: [
            "Unlimited canvas for semester-long study",
            "Generate AI summaries from lecture PDFs",
            "Create flashcards and quizzes automatically",
            "Collaborate on group projects real-time"
        ],
        image: '/hero-preview.png',
        steps: [
            { title: 'Collect', description: 'Drag in lecture slides, PDFs, and web clips onto one infinite board.', icon: '1' },
            { title: 'Connect', description: 'Visually link concepts and create mind maps to understand complex topics.', icon: '2' },
            { title: 'Review', description: 'Use AI to generate practice quizzes and flashcards from your board content.', icon: '3' }
        ],
        testimonials: [
            { quote: 'I finally passed Organic Chemistry thanks to mapping out reactions on NoteSpace.', author: 'Jordan Lee', role: 'Pre-Med Student' }
        ],
        faqs: [
            { question: 'Is it free for students?', answer: 'Yes! The free plan includes unlimited boards and basic AI features.' }
        ]
    },
    {
        slug: 'designers',
        title: 'Infinite Moodboards & Brainstorming',
        subtitle: 'Designers',
        description: 'Break free from rigid slides. Collect inspiration, sketch flows, and iterate on designs in a limitless workspace that adapts to your creativity.',
        features: [
            "Limitless canvas for moodboards",
            "Freeform drawing and sketching",
            "Embed images, videos, and links",
            "Share with clients for feedback"
        ],
        image: '/hero-preview.png',
        steps: [
            { title: 'Gather', description: 'Pull in inspiration from anywhere—images, color palettes, and typography.', icon: '1' },
            { title: 'Ideate', description: 'Sketch freely and iterate on layout ideas without running out of space.', icon: '2' },
            { title: 'Feedback', description: 'Share a link with clients to get feedback directly on specific elements.', icon: '3' }
        ],
        testimonials: [
            { quote: 'The infinite canvas is perfect for my messy creative process. I can see the entire project evolution at a glance.', author: 'Mia Chen', role: 'UX Designer' }
        ],
        faqs: [
            { question: 'Can I export to Figma?', answer: 'You can copy specific elements as SVG or export the board as a high-res image to bring into other tools.' }
        ]
    }
];
